import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import BpmnViewer from "bpmn-js/lib/NavigatedViewer";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Portal,
} from "@mui/material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import { bpmnDiagramThemeSx } from "utils/bpmnDiagramStyles";

interface PreviewAiModelProps {
  uuid: string;
  model: string;
  onApply?: (model: string) => Promise<void>;
}

const setModel = (id: string, idParent: string, model: string) => {
  if (!model || model === "undefined" || !model.trim()) {
    return;
  }
  const canvas = document.getElementById(id) as HTMLCanvasElement;
  const parent = document.getElementById(idParent) as HTMLElement;
  if (!canvas || !parent) return;
  canvas.innerHTML = "";

  const bpmnViewer = new BpmnViewer({
    container: canvas,
    readOnly: true,
    selectable: false,
    additionalModules: [],
  });
  (async () => {
    try {
      await bpmnViewer.importXML(model);
      const _canvas = bpmnViewer.get("canvas") as any;
      const viewbox = _canvas.viewbox();

      if (viewbox) {
        parent.style.height = viewbox.height;
        canvas.style.height = viewbox.height;
        _canvas.zoom("fit-viewport");
      }
    } catch (e) {
      console.warn("BPMN XML parsing error (this is expected during streaming):", e);
    }
  })();
};

const PreviewAiModel: React.FC<PreviewAiModelProps> = ({
  model,
  onApply,
  uuid,
}) => {
  const parentId = useMemo(() => `container-ai-model-${uuid}`, [uuid]);
  const idElement = useMemo(() => `ai-${uuid}`, [uuid]);
  const translations = useTranslations();
  const parentIdElementPreview = useMemo(
    () => `preview-container-ai-model-${uuid}`,
    [uuid]
  );
  const idElementPreview = useMemo(() => `preview-ai-${uuid}`, [uuid]);

  const [openPreview, setOpenPreview] = useState<boolean>(false);

  useEffect(() => {
    setModel(idElement, parentId, model);
  }, [idElement, model, parentId]);

  const handlePreview = () => {
    setOpenPreview(true);
  };

  useLayoutEffect(() => {
    if (openPreview) {
      // console.log(openPreview, "fuori timeout")
      setTimeout(() => {
        setModel(idElementPreview, parentIdElementPreview, model,);
        // console.log(openPreview, "dentro timeout")
      });
      // console.log(openPreview, "sotto tiemout")
    }
  }, [idElementPreview, model, openPreview, parentIdElementPreview]);

  return (
    <React.Fragment>
      <Box sx={{ position: "relative" }} id={parentId}>
        <Box sx={{ position: "absolute", inset: 0, zIndex: 10 }}>
          <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
            <FlowentiButton
              onClick={handlePreview}
              variant="contained"
              size="small"
              sx={{ position: "absolute", bottom: 2, right: 2 }}
            >
              {getTranslation(translations, "ai_assistant.action")}
            </FlowentiButton>
          </Box>
        </Box>
        <Box
          id={idElement}
          sx={{
            ...bpmnDiagramThemeSx,
            "& .bjs-drilldown": { display: "none" },
            "& .djs-minimap": { display: "none" },
            "& .djs-palette": { display: "none" },
            width: 250,
            // height: 200,
            pointerEvents: "none",
          }}
          className="canvas"
        />
      </Box>
      <Portal container={document.body}>
        <Dialog
          sx={{ zIndex: 99999999 }}
          onClose={() => setOpenPreview(false)}
          open={openPreview}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "#000000" : "background.paper",
              color: "text.primary",
            },
          }}
        >
          <DialogContent
            id={parentIdElementPreview}
            sx={{
              height: "75vh",
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "#000000" : "background.paper",
              p: 0,
            }}
          >
            <Box
              id={idElementPreview}
              sx={{
                ...bpmnDiagramThemeSx,
                "& .bjs-drilldown": { display: "none" },
                "& .djs-minimap": { display: "none" },
                "& .djs-palette": { display: "none" },
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              }}
              className="canvas"
            />
          </DialogContent>
          {onApply && (
            <DialogActions
              sx={{
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "#000000" : "background.paper",
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <FlowentiButton
                type="button"
                variant="contained"
                onClick={() => {
                  onApply(model);
                  setOpenPreview(false);
                }}
              >
                {getTranslation(translations, "apply_ai_edit.to.model")}
              </FlowentiButton>
            </DialogActions>
          )}
        </Dialog>
      </Portal>
    </React.Fragment>
  );
};

export default PreviewAiModel;
